import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clubList, clubListSaff, clubListTest, clubRegister, clubUpdate, clubUpdateProfile, clubChangePassword, clubLogin, clubLoginSaff, clubLoginMyClub, clubVerify, clubDelete, clubBulkOperations, listUsersAll, listUsersSAFFAll } from 'src/services/api/API';
import { Club, ClubRegisterRequest, ClubUpdateRequest, ClubUpdateProfileRequest, ClubChangePasswordRequest, ClubLoginRequest, ClubLoginSaffRequest, ClubLoginMyClubRequest, ClubVerifyRequest, ClubPermissions } from 'src/types/api/Club';
import { useAuthStore } from './useAuthStore';

interface ClubState {
  token: string | null;
  tempToken: string | null;
  permissions: ClubPermissions | null;
  clubNameEn: string | null;
  clubNameAr: string | null;
  personNameEn: string | null;
  personNameAr: string | null;
  mobile: string | null;
  email: string | null;
  username: string | null;
  clubId: string | null;
  idSystem: string | null;
  avatar: string | null;
  isClub: boolean;
  isSaff: boolean;
  imageGoogleAuthUrl: string | null;

  filterValueClubs: string;
  rowsPerPageClubs: number;
  pageClubs: number;
  totalPagesClubs: number;
  totalClubsClubs: number;
  clubs: Club[];
  lastFetchedPageClubs: number | null;

  filterValueClubsSaff: string;
  rowsPerPageClubsSaff: number;
  pageClubsSaff: number;
  totalPagesClubsSaff: number;
  totalClubsClubsSaff: number;
  clubsSaff: Club[];
  lastFetchedPageClubsSaff: number | null;

  filterValueClubsTest: string;
  rowsPerPageClubsTest: number;
  pageClubsTest: number;
  totalPagesClubsTest: number;
  totalClubsClubsTest: number;
  clubsTest: Club[];
  lastFetchedPageClubsTest: number | null;

  isLoadingClubs: boolean;
  isLoadingClubsSaff: boolean;
  isLoadingClubsTest: boolean;
  isLoadingAuth: boolean;
  isLoadingLogin: boolean;
  isLoadingLoginSaff: boolean;
  isLoadingLoginMyClub: boolean;
  isLoadingVerify: boolean;
  
  loginError: string | null;
  loginSaffError: string | null;
  loginMyClubError: string | null;
  verifyError: string | null;

  clubNameEnReg: string;
  clubNameArReg: string;
  personNameEnReg: string;
  personNameArReg: string;
  mobileReg: string;
  emailReg: string;
  usernameReg: string;
  registrationError: string | null;
  registrationHasAlert: boolean;
  registrationStatus: 'idle' | 'loading' | 'success' | 'error';
  registrationMessage: string;

  loginMethod: 'regular' | 'saff' | 'myclub';
  loginClubId: string;
  loginPassword: string;
  saffCode: string;
  myClubCode: string;
  googleAuthCode: string;
  loginStep: number;
  captchaValue: string;
  generatedCaptcha: string;
  isCaptchaValid: boolean;
  hasAlert: boolean;
  error: string | null;
  successMessage: string | null;

  selectedTab: string;
  filterValue: string;
  page: number;
  rowsPerPage: number;
  selectedClub: Club | null;
  selectedClubs: Set<string>;
  isEditModalOpen: boolean;

  profileUpdateStatus: 'success' | 'error' | null;
  profileUpdateMessage: string;
  passwordChangeStatus: 'success' | 'error' | null;
  passwordChangeMessage: string;
  clubUpdateStatus: 'success' | 'error' | null;
  clubUpdateMessage: string;
  loginStatus: 'success' | 'error' | null;
  loginMessage: string;

  setToken: (token: string) => void;
  setTempToken: (tempToken: string) => void;
  setPermissions: (permissions: ClubPermissions, clubNameEn: string, clubNameAr: string) => void;
  setClubData: (clubData: {
    clubNameEn: string;
    clubNameAr: string;
    personNameEn: string;
    personNameAr: string;
    mobile: string;
    email: string;
    username: string;
    clubId: string;
    idSystem?: string;
    avatar?: string;
  }) => void;
  setImageGoogleAuthUrl: (url: string) => void;
  clearToken: () => void;
  clearTempToken: () => void;
  clearAll: () => void;

  loginClub: (data: ClubLoginRequest) => Promise<void>;
  loginClubSaff: (data: ClubLoginSaffRequest) => Promise<void>;
  loginClubMyClub: (data: ClubLoginMyClubRequest) => Promise<void>;
  verifyClub: (data: ClubVerifyRequest) => Promise<void>;

  setFilterValueClubs: (value: string) => void;
  setRowsPerPageClubs: (value: number) => void;
  setPageClubs: (value: number) => void;
  fetchClubs: () => Promise<void>;

  setFilterValueClubsSaff: (value: string) => void;
  setRowsPerPageClubsSaff: (value: number) => void;
  setPageClubsSaff: (value: number) => void;
  fetchClubsSaff: () => Promise<void>;

  setFilterValueClubsTest: (value: string) => void;
  setRowsPerPageClubsTest: (value: number) => void;
  setPageClubsTest: (value: number) => void;
  fetchClubsTest: () => Promise<void>;

  setClubNameEnReg: (clubNameEn: string) => void;
  setClubNameArReg: (clubNameAr: string) => void;
  setPersonNameEnReg: (personNameEn: string) => void;
  setPersonNameArReg: (personNameAr: string) => void;
  setMobileReg: (mobile: string) => void;
  setEmailReg: (email: string) => void;
  setUsernameReg: (username: string) => void;
  setRegistrationError: (error: string | null) => void;
  setRegistrationHasAlert: (hasAlert: boolean) => void;
  setRegistrationStatus: (status: 'idle' | 'loading' | 'success' | 'error') => void;
  setRegistrationMessage: (message: string) => void;
  resetRegistrationForm: () => void;

  setLoginMethod: (method: 'regular' | 'saff' | 'myclub') => void;
  setLoginClubId: (clubId: string) => void;
  setLoginPassword: (password: string) => void;
  setSaffCode: (code: string) => void;
  setMyClubCode: (code: string) => void;
  setGoogleAuthCode: (code: string) => void;
  setLoginStep: (step: number) => void;
  setCaptchaValue: (value: string) => void;
  setGeneratedCaptcha: (value: string) => void;
  setIsCaptchaValid: (valid: boolean) => void;
  setHasAlert: (hasAlert: boolean) => void;
  setError: (error: string | null) => void;
  setSuccessMessage: (message: string | null) => void;
  resetLoginForm: () => void;

  setSelectedTab: (tab: string) => void;
  setFilterValue: (value: string) => void;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setSelectedClub: (club: Club | null) => void;
  setSelectedClubs: (clubs: Set<string>) => void;
  setIsEditModalOpen: (isOpen: boolean) => void;
  addSelectedClub: (clubId: string) => void;
  removeSelectedClub: (clubId: string) => void;
  clearSelectedClubs: () => void;

  registerClub: (clubData: ClubRegisterRequest) => Promise<void>;
  updateClubProfile: (profileData: ClubUpdateProfileRequest) => Promise<void>;
  changeClubPassword: (passwordData: ClubChangePasswordRequest) => Promise<void>;
  updateClub: (clubId: string, clubData: ClubUpdateRequest) => Promise<void>;
  deleteClub: (clubId: string) => Promise<void>;
  bulkOperations: (operations: Array<{ action: string; clubId: string; data?: any }>) => Promise<void>;

  resetProfileUpdateStatus: () => void;
  resetPasswordChangeStatus: () => void;
  resetClubUpdateStatus: () => void;
  resetLoginStatus: () => void;
}

export const useClubStore = create<ClubState>()(
  persist(
    (set, get) => ({
      token: null,
      tempToken: null,
      permissions: null,
      clubNameEn: null,
      clubNameAr: null,
      personNameEn: null,
      personNameAr: null,
      mobile: null,
      email: null,
      username: null,
      clubId: null,
      idSystem: null,
      avatar: null,
      isClub: false,
      isSaff: false,
      imageGoogleAuthUrl: null,

      filterValueClubs: '',
      rowsPerPageClubs: 10,
      pageClubs: 1,
      totalPagesClubs: 1,
      totalClubsClubs: 0,
      clubs: [],
      lastFetchedPageClubs: null,

      filterValueClubsSaff: '',
      rowsPerPageClubsSaff: 10,
      pageClubsSaff: 1,
      totalPagesClubsSaff: 1,
      totalClubsClubsSaff: 0,
      clubsSaff: [],
      lastFetchedPageClubsSaff: null,

      filterValueClubsTest: '',
      rowsPerPageClubsTest: 10,
      pageClubsTest: 1,
      totalPagesClubsTest: 1,
      totalClubsClubsTest: 0,
      clubsTest: [],
      lastFetchedPageClubsTest: null,

      isLoadingClubs: false,
      isLoadingClubsSaff: false,
      isLoadingClubsTest: false,
      isLoadingAuth: false,
      isLoadingLogin: false,
      isLoadingLoginSaff: false,
      isLoadingLoginMyClub: false,
      isLoadingVerify: false,
      
      loginError: null,
      loginSaffError: null,
      loginMyClubError: null,
      verifyError: null,

      clubNameEnReg: '',
      clubNameArReg: '',
      personNameEnReg: '',
      personNameArReg: '',
      mobileReg: '',
      emailReg: '',
      usernameReg: '',
      registrationError: null,
      registrationHasAlert: false,
      registrationStatus: 'idle',
      registrationMessage: '',

      loginMethod: 'regular',
      loginClubId: '',
      loginPassword: '',
      saffCode: '',
      myClubCode: '',
      googleAuthCode: '',
      loginStep: 1,
      captchaValue: '',
      generatedCaptcha: '',
      isCaptchaValid: false,
      hasAlert: false,
      error: null,
      successMessage: null,

      selectedTab: 'my-clubs',
      filterValue: '',
      page: 1,
      rowsPerPage: 10,
      selectedClub: null,
      selectedClubs: new Set(),
      isEditModalOpen: false,

      profileUpdateStatus: null,
      profileUpdateMessage: '',
      passwordChangeStatus: null,
      passwordChangeMessage: '',
      clubUpdateStatus: null,
      clubUpdateMessage: '',
      loginStatus: null,
      loginMessage: '',

      setToken: (token: string) => set({ token }),
      setTempToken: (tempToken: string) => set({ tempToken }),
      setPermissions: (permissions: ClubPermissions, clubNameEn: string, clubNameAr: string) => set({ 
        permissions, 
        clubNameEn,
        clubNameAr,
        isClub: permissions?.is_club ?? false,
        isSaff: permissions?.is_saff ?? false
      }),
      setClubData: (clubData) => set({
        clubNameEn: clubData.clubNameEn,
        clubNameAr: clubData.clubNameAr,
        personNameEn: clubData.personNameEn,
        personNameAr: clubData.personNameAr,
        mobile: clubData.mobile,
        email: clubData.email,
        username: clubData.username,
        clubId: clubData.clubId,
        idSystem: clubData.idSystem || null,
        avatar: clubData.avatar || null,
      }),
      setImageGoogleAuthUrl: (url: string) => set({ imageGoogleAuthUrl: url }),
      clearToken: () => set({ token: null }),
      clearTempToken: () => set({ tempToken: null, imageGoogleAuthUrl: null }),
      clearAll: () => set({ 
        token: null, 
        tempToken: null, 
        permissions: null, 
        clubNameEn: null,
        clubNameAr: null,
        personNameEn: null,
        personNameAr: null,
        mobile: null,
        email: null,
        username: null,
        clubId: null,
        idSystem: null,
        avatar: null,
        isClub: false,
        isSaff: false,
        imageGoogleAuthUrl: null,
        clubs: [],
        clubsSaff: [],
        clubsTest: [],
        selectedClubs: new Set(),
        selectedClub: null,
        isLoadingClubs: false,
        isLoadingClubsSaff: false,
        isLoadingClubsTest: false,
        isLoadingAuth: false,
      }),

      loginClub: async (data: ClubLoginRequest) => {
        set({ isLoadingLogin: true, loginStatus: null, loginMessage: '' });
        try {
          const response = await clubLogin(data);
          if (response.success) {
            if (response.token) {
              get().setToken(response.token);
              set({
                permissions: { 
                  is_saff: response.permissions?.is_saff || false, 
                  is_club: true 
                },
                clubNameEn: response.club_name_en || '',
                clubNameAr: response.club_name_en || '',
                isClub: true,
                isSaff: response.permissions?.is_saff || false,
                loginStatus: 'success',
                loginMessage: response.message,
                isLoadingLogin: false,
              });
            } else if (response.temp_token) {
              set({
                tempToken: response.temp_token,
                imageGoogleAuthUrl: response.image_google_auth_url,
                loginStatus: 'success',
                loginMessage: response.message,
                isLoadingLogin: false,
              });
            }
          }
        } catch (error: any) {
          set({
            loginStatus: 'error',
            loginMessage: error.message || 'Login failed',
            loginError: error.message || 'Login failed',
            isLoadingLogin: false,
          });
          throw error;
        }
      },

      loginClubSaff: async (data: ClubLoginSaffRequest) => {
        set({ isLoadingLoginSaff: true, loginStatus: null, loginMessage: '' });
        try {
          const response = await clubLoginSaff(data);
          if (response.success && response.token) {
            get().setToken(response.token);
            
            // Handle both club SAFF users and regular SAFF users
            if (response.data) {
              // Club SAFF user response (has data object with is_club)
              const isClub = response.data.is_club === 1;
              const isSaff = response.permissions.is_saff;
              
              set({
                permissions: { is_saff: isSaff, is_club: isClub },
                clubNameEn: response.data.club_name_en,
                clubNameAr: response.data.club_name_ar,
                personNameEn: response.data.full_name_en || response.data.person_name_en,
                personNameAr: response.data.full_name_ar || response.data.person_name_ar,
                mobile: response.data.mobile,
                email: response.data.email,
                username: response.data.username,
                clubId: response.data.club_id,
                idSystem: response.data.id_system,
                avatar: response.data.avatar,
                isClub: isClub,
                isSaff: isSaff,
                loginStatus: 'success',
                loginMessage: 'Club SAFF login successful',
                isLoadingLoginSaff: false,
              });
            } else {
              // Regular SAFF user response (no data object, not a club)
              const isSaff = response.permissions.is_saff;
              
              set({
                permissions: { is_saff: isSaff, is_club: false },
                clubNameEn: response.name_en || '',
                clubNameAr: response.name_en || '',
                personNameEn: response.name_en || '',
                personNameAr: response.name_en || '',
                isClub: false,
                isSaff: isSaff,
                loginStatus: 'success',
                loginMessage: 'SAFF login successful',
                isLoadingLoginSaff: false,
              });
            }
          }
        } catch (error: any) {
          set({
            loginStatus: 'error',
            loginMessage: error.message || 'SAFF login failed',
            loginSaffError: error.message || 'SAFF login failed',
            isLoadingLoginSaff: false,
          });
          throw error;
        }
      },

      loginClubMyClub: async (data: ClubLoginMyClubRequest) => {
        set({ isLoadingLoginMyClub: true, loginStatus: null, loginMessage: '' });
        try {
          const response = await clubLoginMyClub(data);
          if (response.success && response.token && response.data) {
            get().setToken(response.token);
            
            // Check if user is both club and staff based on response data
            const isClub = response.data.is_club === 1;
            const isSaff = response.permissions.is_saff;
            
            set({
              permissions: { is_saff: isSaff, is_club: isClub },
              clubNameEn: response.data.club_name_en,
              clubNameAr: response.data.club_name_ar,
              personNameEn: response.data.full_name_en || response.data.person_name_en,
              personNameAr: response.data.full_name_ar || response.data.person_name_ar,
              mobile: response.data.mobile,
              email: response.data.email,
              username: response.data.username,
              clubId: response.data.club_id,
              idSystem: response.data.id_system,
              avatar: response.data.avatar,
              isClub: isClub,
              isSaff: isSaff,
              loginStatus: 'success',
              loginMessage: 'MyClub login successful',
              isLoadingLoginMyClub: false,
            });
          }
        } catch (error: any) {
          set({
            loginStatus: 'error',
            loginMessage: error.message || 'MyClub login failed',
            loginMyClubError: error.message || 'MyClub login failed',
            isLoadingLoginMyClub: false,
          });
          throw error;
        }
      },

      verifyClub: async (data: ClubVerifyRequest) => {
        set({ isLoadingVerify: true, loginStatus: null, loginMessage: '' });
        const { tempToken } = get();
        if (!tempToken) {
          set({
            loginStatus: 'error',
            loginMessage: 'No temporary token found',
            isLoadingVerify: false,
          });
          throw new Error('No temporary token found');
        }

        try {
          const response = await clubVerify(data, tempToken);
          if (response.success && response.token) {
            get().setToken(response.token);
            set({
              tempToken: null,
              imageGoogleAuthUrl: null,
              permissions: { is_saff: response.permissions.is_saff, is_club: true },
              clubNameEn: response.club_name_en,
              clubNameAr: response.club_name_en,
              isClub: true,
              isSaff: response.permissions.is_saff,
              loginStatus: 'success',
              loginMessage: 'Verification successful',
              isLoadingVerify: false,
            });
          }
        } catch (error: any) {
          set({
            loginStatus: 'error',
            loginMessage: error.message || 'Verification failed',
            verifyError: error.message || 'Verification failed',
            isLoadingVerify: false,
          });
          throw error;
        }
      },

      setFilterValueClubs: (value: string) => set({ filterValueClubs: value }),
      setRowsPerPageClubs: (value: number) => set({ rowsPerPageClubs: value }),
      setPageClubs: (value: number) => set({ pageClubs: value }),

      fetchClubs: async () => {
        const { token } = useAuthStore.getState();
        if (!token) return;

        set({ isLoadingClubs: true });

        try {
          const response = await clubList(token, { limit: 10000, offset: 0 });
          set({
            clubs: response.clubs || [],
            totalPagesClubs: 1,
            totalClubsClubs: response.totalClubs || 0,
            lastFetchedPageClubs: 1,
            isLoadingClubs: false,
          });
        } catch (error: any) {
          if (error.message?.includes('404') || error.message?.includes('Not Found')) {
            try {
              const userData = await listUsersAll(token, 1, 10000);
              const transformedClubs = userData.users?.map((user: any) => ({
                club_id: user.user_id,
                user_id: user.user_id,
                club_name_en: user.full_name_en || user.username,
                club_name_ar: user.full_name_ar || user.username,
                person_name_en: user.full_name_en || user.username,
                person_name_ar: user.full_name_ar || user.username,
                mobile: user.mobile,
                email: user.email,
                username: user.username,
                created_at: user.created_at,
                is_club: 0,
                user_id_saff: user.user_id_saff
              })) || [];
              set({
                clubs: transformedClubs,
                totalPagesClubs: 1,
                totalClubsClubs: userData.totalUsers || 0,
                lastFetchedPageClubs: 1,
                isLoadingClubs: false,
              });
            } catch (userError: any) {
              set({ 
                clubs: [], 
                totalPagesClubs: 1, 
                totalClubsClubs: 0, 
                lastFetchedPageClubs: 1,
                isLoadingClubs: false 
              });
            }
          } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
            set({ 
              clubs: [], 
              totalPagesClubs: 1, 
              totalClubsClubs: 0, 
              lastFetchedPageClubs: 1,
              isLoadingClubs: false 
            });
          } else {
            set({ isLoadingClubs: false });
          }
        }
      },

      setFilterValueClubsSaff: (value: string) => set({ filterValueClubsSaff: value }),
      setRowsPerPageClubsSaff: (value: number) => set({ rowsPerPageClubsSaff: value }),
      setPageClubsSaff: (value: number) => set({ pageClubsSaff: value }),

      fetchClubsSaff: async () => {
        const { token } = useAuthStore.getState();
        if (!token) return;

        set({ isLoadingClubsSaff: true });

        try {
          const response = await clubListSaff(token, { limit: 10000, offset: 0 });
          set({
            clubsSaff: response.clubs || [],
            totalPagesClubsSaff: 1,
            totalClubsClubsSaff: response.totalClubs || 0,
            lastFetchedPageClubsSaff: 1,
            isLoadingClubsSaff: false,
          });
        } catch (error: any) {
          if (error.message?.includes('404') || error.message?.includes('Not Found')) {
            try {
              const userData = await listUsersSAFFAll(token, 1, 10000);
              const transformedClubs = userData.users?.map((user: any) => ({
                club_id: user.user_id,
                user_id: user.user_id,
                club_name_en: user.full_name_en || user.username,
                club_name_ar: user.full_name_ar || user.username,
                person_name_en: user.full_name_en || user.username,
                person_name_ar: user.full_name_ar || user.username,
                mobile: user.mobile,
                email: user.email,
                username: user.username,
                created_at: user.created_at,
                is_club: 0,
                user_id_saff: user.user_id_saff
              })) || [];
              set({
                clubsSaff: transformedClubs,
                totalPagesClubsSaff: 1,
                totalClubsClubsSaff: userData.totalUsers || 0,
                lastFetchedPageClubsSaff: 1,
                isLoadingClubsSaff: false,
              });
            } catch (userError: any) {
              set({ 
                clubsSaff: [], 
                totalPagesClubsSaff: 1, 
                totalClubsClubsSaff: 0, 
                lastFetchedPageClubsSaff: 1,
                isLoadingClubsSaff: false 
              });
            }
          } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
            set({ 
              clubsSaff: [], 
              totalPagesClubsSaff: 1, 
              totalClubsClubsSaff: 0, 
              lastFetchedPageClubsSaff: 1,
              isLoadingClubsSaff: false 
            });
          } else {
            set({ isLoadingClubsSaff: false });
          }
        }
      },

      setFilterValueClubsTest: (value: string) => set({ filterValueClubsTest: value }),
      setRowsPerPageClubsTest: (value: number) => set({ rowsPerPageClubsTest: value }),
      setPageClubsTest: (value: number) => set({ pageClubsTest: value }),

      fetchClubsTest: async () => {
        console.log('fetchClubsTest: Starting fetch');
        set({ isLoadingClubsTest: true });

        try {
          console.log('fetchClubsTest: Calling clubListTest API');
          const response = await clubListTest({ limit: 10000, offset: 0 });
          console.log('fetchClubsTest: API response:', response);
          
          set({
            clubsTest: response.clubs || [],
            totalPagesClubsTest: 1,
            totalClubsClubsTest: response.totalClubs || 0,
            lastFetchedPageClubsTest: 1,
            isLoadingClubsTest: false,
          });
          console.log('fetchClubsTest: Store updated with clubs:', response.clubs);
        } catch (error: any) {
          console.error('fetchClubsTest: Error:', error);
          if (error.message?.includes('404') || error.message?.includes('Not Found')) {
            set({ 
              clubsTest: [], 
              totalPagesClubsTest: 1, 
              totalClubsClubsTest: 0, 
              lastFetchedPageClubsTest: 1,
              isLoadingClubsTest: false 
            });
          } else {
            set({ isLoadingClubsTest: false });
          }
        }
      },

      setClubNameEnReg: (clubNameEn: string) => set({ clubNameEnReg: clubNameEn }),
      setClubNameArReg: (clubNameAr: string) => set({ clubNameArReg: clubNameAr }),
      setPersonNameEnReg: (personNameEn: string) => set({ personNameEnReg: personNameEn }),
      setPersonNameArReg: (personNameAr: string) => set({ personNameArReg: personNameAr }),
      setMobileReg: (mobile: string) => set({ mobileReg: mobile }),
      setEmailReg: (email: string) => set({ emailReg: email }),
      setUsernameReg: (username: string) => set({ usernameReg: username }),
      setRegistrationError: (error: string | null) => set({ registrationError: error }),
      setRegistrationHasAlert: (hasAlert: boolean) => set({ registrationHasAlert: hasAlert }),
      setRegistrationStatus: (status: 'idle' | 'loading' | 'success' | 'error') => set({ registrationStatus: status }),
      setRegistrationMessage: (message: string) => set({ registrationMessage: message }),
      resetRegistrationForm: () => set({
        clubNameEnReg: '',
        clubNameArReg: '',
        personNameEnReg: '',
        personNameArReg: '',
        mobileReg: '',
        emailReg: '',
        usernameReg: '',
        registrationError: null,
        registrationHasAlert: false,
      }),

      setLoginMethod: (method: 'regular' | 'saff' | 'myclub') => set({ loginMethod: method }),
      setLoginClubId: (clubId: string) => set({ loginClubId: clubId }),
      setLoginPassword: (password: string) => set({ loginPassword: password }),
      setSaffCode: (code: string) => set({ saffCode: code }),
      setMyClubCode: (code: string) => set({ myClubCode: code }),
      setGoogleAuthCode: (code: string) => set({ googleAuthCode: code }),
      setLoginStep: (step: number) => set({ loginStep: step }),
      setCaptchaValue: (value: string) => set({ captchaValue: value }),
      setGeneratedCaptcha: (value: string) => set({ generatedCaptcha: value }),
      setIsCaptchaValid: (valid: boolean) => set({ isCaptchaValid: valid }),
      setHasAlert: (hasAlert: boolean) => set({ hasAlert: hasAlert }),
      setError: (error: string | null) => set({ error: error }),
      setSuccessMessage: (message: string | null) => set({ successMessage: message }),
      resetLoginForm: () => set({
        loginClubId: '',
        loginPassword: '',
        saffCode: '',
        myClubCode: '',
        googleAuthCode: '',
        loginStep: 1,
        captchaValue: '',
        generatedCaptcha: '',
        isCaptchaValid: false,
        hasAlert: false,
        error: null,
        successMessage: null,
      }),

      setSelectedTab: (tab: string) => set({ selectedTab: tab }),
      setFilterValue: (value: string) => set({ filterValue: value }),
      setPage: (page: number) => set({ page: page }),
      setRowsPerPage: (rowsPerPage: number) => set({ rowsPerPage: rowsPerPage }),
      setSelectedClub: (club: Club | null) => set({ selectedClub: club }),
      setSelectedClubs: (clubs: Set<string>) => set({ selectedClubs: clubs }),
      setIsEditModalOpen: (isOpen: boolean) => set({ isEditModalOpen: isOpen }),
      addSelectedClub: (clubId: string) => set((state) => {
        const newSelectedClubs = new Set(state.selectedClubs);
        newSelectedClubs.add(clubId);
        return { selectedClubs: newSelectedClubs };
      }),
      removeSelectedClub: (clubId: string) => set((state) => {
        const newSelectedClubs = new Set(state.selectedClubs);
        newSelectedClubs.delete(clubId);
        return { selectedClubs: newSelectedClubs };
      }),
      clearSelectedClubs: () => set({ selectedClubs: new Set() }),

      registerClub: async (clubData: ClubRegisterRequest) => {
        const { token } = useAuthStore.getState();
        if (!token) {
          set({ registrationStatus: 'error', registrationMessage: 'No authentication token found' });
          return;
        }

        try {
          set({ registrationStatus: 'loading' });
          const response = await clubRegister(clubData, token);
          set({ 
            registrationStatus: 'success', 
            registrationMessage: response.message || 'Club registered successfully' 
          });
        } catch (error: any) {
          set({ 
            registrationStatus: 'error', 
            registrationMessage: error.message || 'Failed to register club' 
          });
          throw error;
        }
      },

      updateClubProfile: async (profileData: ClubUpdateProfileRequest) => {
        const { token } = useAuthStore.getState();
        if (!token) {
          set({ profileUpdateStatus: 'error', profileUpdateMessage: 'No authentication token found' });
          return;
        }

        try {
          const response = await clubUpdateProfile(profileData, token);
          set({ 
            profileUpdateStatus: 'success', 
            profileUpdateMessage: response.message || 'Profile updated successfully' 
          });
        } catch (error: any) {
          set({ 
            profileUpdateStatus: 'error', 
            profileUpdateMessage: error.message || 'Failed to update profile' 
          });
        }
      },

      changeClubPassword: async (passwordData: ClubChangePasswordRequest) => {
        const { token } = useAuthStore.getState();
        if (!token) {
          set({ passwordChangeStatus: 'error', passwordChangeMessage: 'No authentication token found' });
          return;
        }

        try {
          const response = await clubChangePassword(passwordData, token);
          set({ 
            passwordChangeStatus: 'success', 
            passwordChangeMessage: response.message || 'Password changed successfully' 
          });
        } catch (error: any) {
          set({ 
            passwordChangeStatus: 'error', 
            passwordChangeMessage: error.message || 'Failed to change password' 
          });
        }
      },

      updateClub: async (clubId: string, clubData: ClubUpdateRequest) => {
        const { token } = useAuthStore.getState();
        if (!token) {
          set({ clubUpdateStatus: 'error', clubUpdateMessage: 'No authentication token found' });
          return;
        }

        try {
          const response = await clubUpdate(clubId, clubData, token);
          set({ 
            clubUpdateStatus: 'success', 
            clubUpdateMessage: response.message || 'Club updated successfully' 
          });
        } catch (error: any) {
          set({ 
            clubUpdateStatus: 'error', 
            clubUpdateMessage: error.message || 'Failed to update club' 
          });
        }
      },

      deleteClub: async (clubId: string) => {
        const { token } = useAuthStore.getState();
        if (!token) {
          set({ clubUpdateStatus: 'error', clubUpdateMessage: 'No authentication token found' });
          return;
        }

        try {
          const response = await clubDelete(clubId, token);
          set({ 
            clubUpdateStatus: 'success', 
            clubUpdateMessage: response.message || 'Club deleted successfully' 
          });
          get().fetchClubs();
        } catch (error: any) {
          set({ 
            clubUpdateStatus: 'error', 
            clubUpdateMessage: error.message || 'Failed to delete club' 
          });
        }
      },

      bulkOperations: async (operations: Array<{ action: string; clubId: string; data?: any }>) => {
        const { token } = useAuthStore.getState();
        if (!token) {
          set({ clubUpdateStatus: 'error', clubUpdateMessage: 'No authentication token found' });
          return;
        }

        try {
          const response = await clubBulkOperations(operations, token);
          set({ 
            clubUpdateStatus: 'success', 
            clubUpdateMessage: response.message || 'Bulk operations completed successfully' 
          });
          get().fetchClubs();
        } catch (error: any) {
          set({ 
            clubUpdateStatus: 'error', 
            clubUpdateMessage: error.message || 'Failed to perform bulk operations' 
          });
        }
      },

      resetProfileUpdateStatus: () => set({ profileUpdateStatus: null, profileUpdateMessage: '' }),
      resetPasswordChangeStatus: () => set({ passwordChangeStatus: null, passwordChangeMessage: '' }),
      resetClubUpdateStatus: () => set({ clubUpdateStatus: null, clubUpdateMessage: '' }),
      resetLoginStatus: () => set({ loginStatus: null, loginMessage: '' }),
    }),
    {
      name: 'club-storage',
      storage: localStorage as any,
      partialize: (state) => ({
        token: state.token,
        tempToken: state.tempToken,
        permissions: state.permissions,
        clubNameEn: state.clubNameEn,
        clubNameAr: state.clubNameAr,
        personNameEn: state.personNameEn,
        personNameAr: state.personNameAr,
        mobile: state.mobile,
        email: state.email,
        username: state.username,
        clubId: state.clubId,
        idSystem: state.idSystem,
        avatar: state.avatar,
        isClub: state.isClub,
        isSaff: state.isSaff,
        imageGoogleAuthUrl: state.imageGoogleAuthUrl,
      }),
    }
  )
);
